import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useCompany } from '../../../CompanyContext';
import SupportTicketForm from '../components/SupportTicketForm';
import SupportTicketList from '../components/SupportTicketList';
import { createSupportTicket, listMySupportTickets, subscribeMySupportTickets } from '../services/supportTicketsService';
import PaginationBar from '../../../shared/components/pagination/PaginationBar';

import { ListRowSkeletonStack } from '../../../components/loading/skeletons';
import '../styles/supportTickets.css';

export default function SupportTicketsPage() {
  const navigate = useNavigate();
  const { user, companyData } = useAuth();
  const { companyName, companyId: companyIdFromContext } = useCompany();

  const companyId = useMemo(
    () => user?.companyId || companyData?.id || companyIdFromContext || null,
    [user?.companyId, companyData?.id, companyIdFromContext]
  );

  const actor = useMemo(() => {
    if (!user) return null;
    const name =
      user?.name ||
      `${user?.firstName || ''} ${user?.lastName || ''}`.trim() ||
      user?.displayName ||
      user?.email ||
      'User';
    return {
      uid: user.uid,
      email: user.email || null,
      name,
      phone: user.phone || null,
      company: {
        name: companyData?.name || companyName || null,
      },
    };
  }, [user, companyData?.name, companyName]);

  const [tickets, setTickets] = useState(null);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 10;

  useEffect(() => {
    if (!companyId || !user?.uid) return undefined;

    setLoadingTickets(true);
    const unsub = subscribeMySupportTickets({
      companyId,
      clientUid: user.uid,
      onData: (items) => {
        setTickets(items);
        setLoadingTickets(false);
      },
      onError: (err) => {
        console.error('Error loading tickets:', err);
        setError('Could not load tickets. Please try again.');
        setLoadingTickets(false);
      },
    });

    return () => unsub();
  }, [companyId, user?.uid]);

  // Reset + clamp pagination when ticket list changes
  useEffect(() => {
    setPage(0);
  }, [companyId, user?.uid]);

  useEffect(() => {
    const total = tickets?.length || 0;
    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
    if (page > pageCount - 1) setPage(pageCount - 1);
  }, [tickets?.length, page]);

  const pagedTickets = useMemo(() => {
    const items = Array.isArray(tickets) ? tickets : [];
    const start = page * PAGE_SIZE;
    return items.slice(start, start + PAGE_SIZE);
  }, [tickets, page]);

  const handleCreate = async ({ subject, category, priority, message }) => {
    setError('');
    setSuccess('');
    setSubmitting(true);
    try {
      if (!companyId) throw new Error('Missing companyId');
      await createSupportTicket({
        companyId,
        clientUser: actor,
        subject,
        category,
        priority,
        message,
        authorType: 'company',
      });
      const items = await listMySupportTickets({ companyId });
      setTickets(items);
      setSuccess('Ticket submitted. You can track updates below.');
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Failed to submit ticket.');
    } finally {
      setSubmitting(false);
    }
  };

  const openTicket = (t) => {
    navigate(`/app/overview/support/tickets/${t.id}`);
  };

  return (
    <div className="st-page">
      <div className="st-shell">
        <div className="st-hero">
          <h2 className="st-heroTitle">Support</h2>
          <p className="st-heroSub">
            Submit a ticket to Neat Schedule support and track the conversation.
          </p>
        </div>

        <div className="st-grid">
          <div className="st-card">
            <div className="st-cardHeader">
              <h3 className="st-cardTitle">Create a ticket</h3>
              <span className="st-pill">Replies arrive here</span>
            </div>

            {error ? <div className="st-alert st-alertError">{error}</div> : null}
            {success ? <div className="st-alert st-alertSuccess">{success}</div> : null}

            <SupportTicketForm submitting={submitting} onSubmit={handleCreate} />
          </div>

          <div className="st-card">
            <div className="st-cardHeader">
              <h3 className="st-cardTitle">My tickets</h3>
              {loadingTickets ? null : <span className="st-pill">{tickets?.length || 0}</span>}
            </div>
            {loadingTickets ? (
              <ListRowSkeletonStack rows={5} variant="compact" label="Loading tickets" />
            ) : (
              <>
                <SupportTicketList tickets={pagedTickets} onOpenTicket={openTicket} />
                {(tickets?.length || 0) > PAGE_SIZE ? (
                  <PaginationBar
                    page={page}
                    pageSize={PAGE_SIZE}
                    total={tickets?.length || 0}
                    onPageChange={setPage}
                    ariaLabel="My tickets pagination"
                    className="st-pagination"
                  />
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

